/**
 * The Extra support list and the director's extra visit, from the app (D-1020-EXTRA). Reads are the API's; a press is
 * never re-sent by the app itself (lib/api.ts).
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import type { BookedStart } from '@shared/day-bend';
import type { ExtraBookRequest, ExtraSpotsResponse, ExtraSupportListResponse } from '@shared/extra-support';

/** Who is on the list (a label on the line; the checkbox on the Housekeeping section). */
export function useExtraSupportIds(enabled = true) {
  return useQuery({
    queryKey: ['extra-support'],
    queryFn: () => apiFetch<ExtraSupportListResponse>('/api/extra-support'),
    enabled,
    staleTime: 30_000,
    select: (d) => new Set(d.on),
  });
}

/** The open spots, today and the coming days, with who could be offered each. */
export function useOpenSpots(enabled = true, days = 7) {
  return useQuery({
    queryKey: ['extra-spots', days],
    queryFn: () => apiFetch<ExtraSpotsResponse>(`/api/housekeeping/extra-spots?days=${days}`),
    enabled,
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });
}

export interface ExtraBooked {
  appointmentId: string;
  position: number;
  message: string;
  bent?: BookedStart;
  communicationId?: string | null;
  emailNotQueued?: string;
}

export const postExtra = (body: ExtraBookRequest) => apiFetch<ExtraBooked>('/api/housekeeping/extra', { method: 'POST', body: JSON.stringify(body) });

export const putExtraSupport = (tenantId: string, on: boolean) =>
  apiFetch<{ on: boolean; changed: boolean }>(`/api/extra-support/${encodeURIComponent(tenantId)}`, { method: 'PUT', body: JSON.stringify({ on }) });

export const postNotBookedDone = (transactionId: string) => apiFetch<{ changed: boolean }>('/api/housekeeping/not-booked/done', { method: 'POST', body: JSON.stringify({ transactionId }) });

/** After the visit is written or the list changed: every read that shows it is asked again. */
export function useRefreshExtra() {
  const qc = useQueryClient();
  return () => {
    for (const k of ['today', 'attention', 'appointments', 'slots', 'tenant', 'booking-days', 'booking-preview', 'planning', 'extra-support', 'extra-spots', 'house-today', 'people', 'person']) void qc.invalidateQueries({ queryKey: [k] });
  };
}
